import { createContext, useContext, useState, useEffect } from "react";
import api from "../api";

const SystemConfigContext = createContext({});

const SystemConfigProvider = ({ children }) => {
    const [logo, setLogo] = useState(() => {
        return localStorage.getItem("systemLogo") || null;
    });
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [systemStatus, setSystemStatus] = useState(null);

    const fetchSystemConfig = async () => {
        try {
            setLoading(true);
            const response = await api.configs.getSystemConfig();
            setConfig(response);

            if (response.logoUrl) {
                setLogo(response.logoUrl);
                localStorage.setItem("systemLogo", response.logoUrl);
            } else {
                setLogo(null);
                localStorage.removeItem("systemLogo");
            }
        } catch (error) {
            console.error("Error fetching system config:", error);
            const cachedLogo = localStorage.getItem("systemLogo");
            if (cachedLogo) {
                setLogo(cachedLogo);
            } else {
                setLogo(null);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchSystemStatus = async () => {
        try {
            const response = await api.configs.getSystemStatus();
            setSystemStatus(response);
        } catch (error) {
            console.error("Error fetching system status:", error);
        }
    };

    useEffect(() => {
        const cachedLogo = localStorage.getItem("systemLogo");
        if (cachedLogo) {
            setLogo(cachedLogo);
        }
        fetchSystemConfig();
        fetchSystemStatus();

        const statusInterval = setInterval(fetchSystemStatus, 60000);
        return () => clearInterval(statusInterval);
    }, []);

    const context = {
        logo,
        loading,
        config,
        systemStatus,
        refreshConfig: fetchSystemConfig,
        refreshStatus: fetchSystemStatus,
    };

    return (
        <SystemConfigContext.Provider value={context}>
            {children}
        </SystemConfigContext.Provider>
    );
};

const useSystemConfig = () => useContext(SystemConfigContext);
const useSystemLogo = () => {
    const { logo, loading } = useContext(SystemConfigContext);
    return { logo, loading };
};

export { SystemConfigProvider, useSystemConfig, useSystemLogo };
export default SystemConfigContext;
